import { Component, Input } from "@angular/core";
import { DashboardFulfillmentFunnelStage } from "../../Dto/DashboardFulfillmentFunnelResponse";

@Component({
  standalone: true,
  selector: "app-dashboard-fulfillment-funnel",
  templateUrl: "./DashboardFulfillmentFunnel.html",
  styleUrl: "./DashboardFulfillmentFunnel.scss",
})
export class DashboardFulfillmentFunnel {
  private readonly funnelCenterY = 190;
  private readonly funnelMaxHalfHeight = 122;
  private readonly funnelMinHalfHeight = 18;
  private readonly funnelStartX = 24;
  private readonly funnelEndX = 896;

  @Input({ required: true }) stages: DashboardFulfillmentFunnelStage[] = [];
  @Input({ required: true }) loading = false;
  @Input({ required: true }) selectedPeriodLabel = "";

  formatValue(value: number): string {
    return new Intl.NumberFormat("en-US").format(value);
  }

  formatFunnelPercentage(value: number): string {
    return `${value.toFixed(1)}%`;
  }

  getFunnelBoundaryIndexes(): number[] {
    return Array.from({ length: this.stages.length + 1 }, (_, index) => index);
  }

  getFunnelBoundaryX(boundaryIndex: number): number {
    const stageCount = Math.max(this.stages.length, 1);
    return Math.round(this.funnelStartX + ((this.funnelEndX - this.funnelStartX) / stageCount) * boundaryIndex);
  }

  getFunnelSegmentLabelX(stageIndex: number): number {
    return this.getFunnelSegmentCenterX(stageIndex);
  }

  getFunnelSegmentMetricX(stageIndex: number): number {
    const segmentWidth = this.getFunnelSegmentWidth();
    return Math.round(this.getFunnelBoundaryX(stageIndex + 1) - segmentWidth * 0.08);
  }

  getFunnelSegmentMetricValueX(stageIndex: number, stage: DashboardFulfillmentFunnelStage): number {
    const percentageLength = this.formatFunnelPercentage(stage.percentageOfBase).length;
    const valueLength = this.formatValue(stage.value).length;
    const monacoApproximateCharWidth = 8;
    const centerOffset = Math.max(0, ((percentageLength - valueLength) * monacoApproximateCharWidth) / 2);

    return Math.round(this.getFunnelSegmentMetricX(stageIndex) - centerOffset);
  }

  getFunnelPath(): string {
    if (this.stages.length === 0) {
      return "";
    }

    const topPoints = this.stages.map((stage, index) => {
      const x = this.getFunnelBoundaryX(index);
      const y = this.funnelCenterY - this.getFunnelStageHalfHeight(stage);
      return `${x},${y}`;
    });
    const finalStage = this.stages[this.stages.length - 1];
    topPoints.push(
      `${this.getFunnelBoundaryX(this.stages.length)},${this.funnelCenterY - this.getFunnelStageHalfHeight(finalStage)}`,
    );

    const bottomPoints = this.stages
      .map((stage, index) => {
        const x = this.getFunnelBoundaryX(index);
        const y = this.funnelCenterY + this.getFunnelStageHalfHeight(stage);
        return `${x},${y}`;
      })
      .reverse();
    bottomPoints.unshift(
      `${this.getFunnelBoundaryX(this.stages.length)},${this.funnelCenterY + this.getFunnelStageHalfHeight(finalStage)}`,
    );

    return `M ${topPoints.join(" L ")} L ${bottomPoints.join(" L ")} Z`;
  }

  getFunnelLabelLines(title: string): string[] {
    if (title === "Testimony Submitted") {
      return ["Testimony", "Submitted"];
    }

    return [title];
  }

  getSkeletonFunnelStages(): number[] {
    return [1, 2, 3, 4, 5];
  }

  private getFunnelStageHalfHeight(stage: DashboardFulfillmentFunnelStage): number {
    const normalizedPercentage = Math.max(0, Math.min(100, stage.percentageOfBase));
    const scaledHeight = (normalizedPercentage / 100) * this.funnelMaxHalfHeight;
    return Math.round(Math.max(this.funnelMinHalfHeight, scaledHeight));
  }

  private getFunnelSegmentCenterX(stageIndex: number): number {
    return Math.round((this.getFunnelBoundaryX(stageIndex) + this.getFunnelBoundaryX(stageIndex + 1)) / 2);
  }

  private getFunnelSegmentWidth(): number {
    const stageCount = Math.max(this.stages.length, 1);
    return (this.funnelEndX - this.funnelStartX) / stageCount;
  }
}
